import { Arr, assert } from '@deqo/foundation';
import { invokeLater } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Attribute, ComponentType, ObjectManager, ObjectUpdate } from '../../../concepts/objects';
import { FontStyle, UniversalSurfaceRenderer } from '../../../platform/UniversalSurfaceRenderer';
import { Line } from '../../../platform/geom/Line';
import { Point } from '../../../platform/geom/Point';
import { Polyline } from '../../../platform/geom/Polyline';
import { Rect } from '../../../platform/geom/Rect';
import { KeyInput } from '../../../platform/input/KeyInput';
import { Keys } from '../../../platform/input/Keys';
import { MouseButtons, MouseInput } from '../../../platform/input/MouseInput';
import { Colors } from '../../../ui';
import { resolve } from '../../Container';
import { AutoLayoutEngine } from '../../packages/autoLayout/AutoLayoutEngine';
import { AutoLayoutPotentialInsert } from '../../packages/autoLayout/LayoutStrategy';
import { Camera } from '../Camera';
import { EditorSession } from '../EditorSession';
import { GridSystem } from '../GridSystem';
import { PointerInfo } from '../PointerInfo';
import { RootOverride } from '../RootOverride';
import { Selection } from '../Selection';
import { beginMutating } from '../commands/ManipulationCommands';
import { place, swallow } from '../commands/OrderCommands';
import { select } from '../commands/SelectionCommands';
import { DropInfo, DropTarget } from '../entities/DropTarget';
import { Phys } from '../entities/Phys';
import { Physical, RelativeTo, } from '../entities/Physical';
import { Physicals } from '../entities/Physicals';
import { RectangularPhysical } from '../entities/RectangularPhysical';
import { Root } from '../entities/Root';
import { HistoryManager } from '../history/HistoryManager';
import { SpatialIndex } from '../utilities/SpatialIndex';
import { EffectDirector, Effects } from '../visualization/EffectDirector';
import { ToolBehavior, ToolSettings } from './ToolBehavior';
import { toolEquip } from './ToolCommands';
import { ToolManager } from './ToolManager';
import { ToolOverlay } from './ToolOverlay';

export const IsCreating = new Attribute<boolean, Physical>('isCreating', false);

export class CreateTransformableDragParams {

    public shouldFix?: boolean;
    public shouldSnap?: boolean;
    public shouldMirror?: boolean;
    public from: Point;
    public to: Point;

    constructor(from: Point) {
        this.from = this.to = from;
    }

    public distance() {
        return this.from.distanceTo(this.to);
    }

    public rect(grid: GridSystem) {

        const line = this.line(grid);
        return Rect.fromPoints([line.p1, line.p2]);
    }

    public line(grid: GridSystem) {
        let o = this.from;
        let t = this.to;

        if (this.shouldFix) t = this.fix(o, t);
        if (this.shouldSnap) t = grid.snap(t);
        if (this.shouldMirror) o = this.reflect(o, t);

        return new Line(o, t);
    }

    private fix(o: Point, t: Point) {
        const v = Point.vector(o, t);
        const q = v.quadrant();
        const a = axes[q];
        return v.project(a).add(o);
    }

    private reflect(o: Point, t: Point) {
        return o.add(Point.vector(o, t).inverse());
    }
}

const axes = {
    0: new Point(1, 1).normalize(),
    1: new Point(-1, 1).normalize(),
    2: new Point(-1, -1).normalize(),
    3: new Point(1, -1).normalize(),
};

type CreateMode = 'drag' | 'drop' | 'plop';

abstract class AbstractCreatePhysicalToolBehavior<T extends Physical> extends ToolBehavior {

    @resolve(AutoLayoutEngine) protected autoLayout!: AutoLayoutEngine;
    @resolve(Camera) protected camera!: Camera;
    @resolve(CommandDispatcher) protected dispatch!: CommandDispatcher;
    @resolve(EffectDirector) protected effects!: EffectDirector;
    @resolve(GridSystem) protected grid!: GridSystem;
    @resolve(HistoryManager) protected history!: HistoryManager;
    @resolve(ObjectManager) protected objects!: ObjectManager;
    @resolve(ToolOverlay) private overlay!: ToolOverlay;
    @resolve(EditorSession) protected session!: EditorSession;
    @resolve(SpatialIndex) protected spatial!: SpatialIndex;
    @resolve(ToolManager) protected tools!: ToolManager;

    protected settings: ToolSettings = {};

    private drag = null as CreateTransformableDragParams | null;
    private subject: T | null = null;
    private edit = true;
    private actualMode: CreateMode;
    private potentialInsert = null as AutoLayoutPotentialInsert;
    private potentialDrop = null as DropInfo;

    protected constructor(
        private readonly type: ComponentType<T>,
        private readonly defaultParam: string = 'plop:edit') {
        super();
    }

    protected get mode() {
        return this.actualMode;
    }

    public init(settings: ToolSettings, param: string) {
        this.burden(
            this.overlay.add(gfx => {
                if (!this.subject?.alive || !this.subject.mounted()) return;
                this.paintSubject(this.subject, gfx);
            })
        );
        this.settings = settings;
        const [mode, edit] = (param || this.defaultParam).split(/:/g).map(x => x.trim()) as any;
        this.actualMode = mode || 'plop';
        this.edit = edit === 'edit';
        if (mode === 'drop') {
            this.doPlop(this.session.get(PointerInfo).position, false);
            return 'creating';
        }
    }

    public getPreferredCursor(state: string) {
        if (state === 'idle' || state === 'creating') {
            return 'crosshair';
        }
    }

    public onKeyDown(state: string, ki: KeyInput): string | void {
        if (this.mode === 'drag' && state === 'creating') {
            if (ki.key === Keys.ESCAPE) {
                this.history.cancelNextIfKey(`create:${this.subject?.id}`);
                this.dispatch(toolEquip, ['default']);
                return 'idle';
            }
            else {
                this.applyModifiers(ki);
                this.doDrag(false);
            }
        }
    }

    public onKeyUp(state: string, ki: KeyInput): string | void {
        if (this.mode === 'drag' && state === 'creating') {
            this.applyModifiers(ki);
            this.doDrag(false);
        }
    }

    public onMouseDown(state: string, mi: MouseInput): string | void {
        if (state !== 'idle') return;
        if (mi.button !== MouseButtons.Main) return;
        assert(!this.subject, 'AbstractCreatePhysicalToolBehavior: creating with left over subject?');

        if (this.mode === 'drag') {
            // Start point is snapped at point of click to prevent jank
            const mpt = this.camera.toModel(mi.position);
            const spt = this.shouldForceSnap() && !mi.modifiers.ctrl ? this.grid.snap(mpt) : mpt;
            this.drag = new CreateTransformableDragParams(spt);
            this.applyModifiers(mi);
        }
        else {
            this.doPlop(mi.position, false);
        }

        return 'creating';
    }

    public onMouseMove(state: string, mi: MouseInput): string | void {
        if (state !== 'creating') return;

        if (this.mode === 'drag') {
            assert(!!this.drag, 'CreatePhysicalToolBehavior: creating without action?');
            this.drag.to = this.camera.toModel(mi.position);
            this.applyModifiers(mi);
            this.doDrag(false);
        }
        else {
            assert(!!this.subject, 'CreatePhysicalToolBehavior: creating without subject?');
            this.doPlop(mi.position, false);
        }
    }

    public onMouseUp(state: string, mi: MouseInput): string | void {
        if (state !== 'creating') return;

        if (this.shouldMultiClickDrag() && this.drag.distance() < 5) {
            return 'creating';
        }

        const shouldDrag = this.mode === 'drag' && this.drag.distance() >= 5;

        if (shouldDrag) {
            this.doDrag(true);
        }
        else {
            this.doPlop(mi.position, true);
        }

        if (shouldDrag || !mi.modifiers.shift) {
            this.dispatch(toolEquip, ['default']);
            this.dispatch(select, [[this.subject.id]]);

            if (this.edit) {
                const { id } = this.subject;
                this.burden(invokeLater(() => this.dispatch(beginMutating, [id])));
            }
        }

        this.drag = null;
        this.subject = null;

        return 'idle';
    }

    private doDrag(final?: boolean) {
        assert(!!this.drag, 'CreatePhysicalToolBehavior: creating without gesture?');

        this.objects.update(u => {
            const subject = this.subject ?? (this.subject = this.createSubject(u));
            IsCreating.set(subject, true);
            u.set('key', `edit:${subject.id}`);
            u.set('rolling', !final);
            if (!subject.parent) {
                const root = this.session.get(RootOverride) ?? this.objects.get('root', Root);
                root.add(subject);
            }

            this.applyDrag(subject, this.drag);
            this.updateSubject(subject);

            if (final) {
                IsCreating.set(subject, false);
                this.finalizeSubject(subject);
                this.placeSubject(subject);
            }
        });

        this.overlay.repaint();
    }

    private applyDrop() {
        const { target, command } = this.potentialDrop ?? {};
        if (!target || !command) return;
        this.dispatch(this.potentialDrop.command, [target.id, [this.subject.id]]);
        this.potentialDrop = null;
    }

    private doPlop(vpt: Point, final: boolean) {
        this.objects.update(u => {
            // If not created yet, create
            const subject = this.subject = (this.subject ?? this.createSubject(u));
            IsCreating.set(subject, true);
            // Set props (requires id)
            u.set('key', `edit:${subject.id}`);
            u.set('rolling', !final);
            // Move to relative position
            this.applyPlop(subject, vpt);
            this.updateSubject(subject);
            // Support AutoLayout / Drop
            this.potentialInsert = this.autoLayout.queryPotentialInsert(this.camera.toModel(vpt), new Set([subject]));
            this.updateDrop(vpt);
            // If the final move, place
            if (final) {
                IsCreating.set(subject, false);
                this.finalizeSubject(subject);

                // Support AutoLayout / Drop
                if (this.potentialInsert) {
                    const { container: target, at } = this.potentialInsert;
                    this.autoLayout.insert(target, at, new Set([subject]));
                    this.potentialInsert = null;
                }

                this.applyDrop();
                this.placeSubject(subject);
            }
        });

        this.overlay.repaint();
    }

    protected createSubject(u: ObjectUpdate) {
        const subject = u.create(this.type);
        this.resolveRoot().add(subject);
        this.session.apply(Selection, x => x.replace([subject]));
        // Animate
        if (this.mode !== 'drag') {
            this.effects.trigger(Effects.popIn, subject);
        }
        return subject;
    }

    protected updateSubject(_subject: T): void {
    }

    protected placeSubject(subject: T) {
        if (this.shouldConsume()) {
            const placeInto = this.findDragPlaceInto(subject);
            const placeBefore = this.findDragPlaceBefore(placeInto, subject);

            placeInto.addBefore(placeBefore, subject.removeSelf());
            subject.moveBy(placeInto.position(RelativeTo.Model).multiply(-1));

            this.dispatch(swallow, [subject.id]);
        }
        else {
            this.dispatch(place, [subject.id]);
        }
    }

    protected finalizeSubject(_subject: T): void {
    }

    protected applyDrag(subject: T, drag: CreateTransformableDragParams) {
        const root = this.resolveRoot();
        const r = drag.rect(this.grid);
        const rpt = root.toRelative(r.topLeft());
        if (subject instanceof RectangularPhysical) {
            subject.x = rpt.x;
            subject.y = rpt.y;
            subject.resize(r.width, r.height);
        }
    }

    protected applyPlop(subject: T, vpt: Point) {
        const rpt = this.resolveRoot().toRelative(this.camera.toModel(vpt)).subtract(subject.size().divide(2));
        const spt = this.shouldForceSnap() ? this.grid.snap(rpt) : rpt;
        subject.moveTo(spt);
    }

    protected shouldForceSnap() {
        return true;
    }

    protected shouldForceFix() {
        return false;
    }

    protected shouldConsume() {
        return false;
    }

    protected shouldMultiClickDrag() {
        return false;
    }

    protected paintSubject(subject: T, gfx: UniversalSurfaceRenderer) {
        this.paintArea(gfx);
        this.paintAutoLayout(gfx);
        this.paintDrop(gfx);
    };

    private applyModifiers(xi: MouseInput | KeyInput) {
        this.drag.shouldSnap = this.shouldForceSnap() && !xi.modifiers.ctrl;
        this.drag.shouldFix = this.shouldForceFix() || xi.modifiers.shift;
        this.drag.shouldMirror = xi.modifiers.alt;
    }

    private resolveRoot() {
        return this.session.get(RootOverride) ?? this.objects.get('root', Root);
    }

    private findDragPlaceInto(subject: Physical) {
        const rect = subject.bounds(RelativeTo.Model);
        const hits = Physicals.bottomToTop(this.spatial.lookup(rect));
        for (const h of hits) {
            if (h === subject) continue;
            if (h.bounds(RelativeTo.Model).contains(rect)) {
                return h;
            }
        }
        return this.resolveRoot();
    }

    private findDragPlaceBefore(placeInto: Physical, subject: Physical) {
        const rect = subject.bounds(RelativeTo.Model);
        const hits = new Set(
            Array.from(this.spatial.lookup(rect))
                .filter(x => rect.contains(x.bounds(RelativeTo.Model)))
        );

        const filtered = Physicals.top(hits)
            .filter(x => x !== subject && x.parent === placeInto);

        return Arr.first(Physicals.topToBottom(filtered));
    }

    private updateDrop(vpt: Point) {
        const mpt = this.camera.toModel(vpt);
        const target = Phys.from(this.spatial.lookup(mpt, DropTarget)).whereNotIn([this.subject]).highest()?.as(DropTarget);
        if (target) {
            this.potentialDrop = target.queryDrop(mpt, [this.subject]);
        }
        else {
            this.potentialDrop = null;
        }
    }


    private paintArea(gfx: UniversalSurfaceRenderer) {
        if (!this.subject?.alive || !this.subject.mounted()) return;
        if (this.mode !== 'drag') return;

        const size = this.subject.size();
        const mt = this.camera.modelViewMatrix.multiply(this.subject.transform(RelativeTo.Model));
        const bounds = Rect.fromPoints(mt.apply(new Rect(0, 0, size.w, size.h)));

        gfx.strokeStyle = Colors.primaryLight;
        gfx.lineWidth = 1.5;
        gfx.strokeRect(...bounds.toTuple());

        {
            const sizeAt = bounds.pointAt(.5, 1).add([0, 10]);
            const sizeRect = new Rect(sizeAt.x - 200, sizeAt.y, 400, 20).round();
            gfx.fontStyle = FontStyle.create({ size: 16, family: 'Rubik' });

            gfx.fillStyle = Colors.primaryLight;
            gfx.fillTextRect(`${Math.round(size.w)} × ${Math.round(size.h)}`,
                sizeRect.left,
                sizeRect.top,
                sizeRect.width,
                sizeRect.height,
                'center',
                'top',
                16);
        }
    };

    private paintAutoLayout(gfx: UniversalSurfaceRenderer) {
        const { container, at, guides, style } = this.potentialInsert ?? {};
        if (!container || !at || !guides) return;

        gfx.setTransform(this.camera.modelViewMatrix);
        gfx.strokeStyle = Colors.primaryLight;
        gfx.lineCap = 'round';
        gfx.lineWidth = style === 'thin' ? 3 : 6;

        for (const g of guides) {
            gfx.beginPath();
            gfx.moveTo(g.p1.x, g.p1.y);
            gfx.lineTo(g.p2.x, g.p2.y);
            gfx.stroke();
        }
    };

    private paintDrop = (gfx: UniversalSurfaceRenderer) => {
        const { guides } = this.potentialDrop ?? {};
        if (!guides) return;

        gfx.setTransform(this.camera.modelViewMatrix);
        gfx.strokeStyle = Colors.primaryLight;
        gfx.lineCap = 'round';

        for (const g of guides) {
            gfx.lineWidth = (g.style ?? 'default') === 'default' ? 3 : 6;
            gfx.beginPath();
            gfx.stroke(Polyline.from(g.points).toPath().toPath2D());
        }
    };
}

// eslint-disable-next-line max-len
export const CreatePhysicalToolBehavior = <T extends Physical>(type: ComponentType<T>, defaultParam?: string) =>
    class extends AbstractCreatePhysicalToolBehavior<T> {
        constructor() {
            super(type, defaultParam);
        }
    };
